import { h } from '../lib/dom.js';
import { getState, setSilent, fmtNum, fmtReporting, toReporting, totalUSDFor, CONFIG } from '../store.js';
import {
  setMoveField, setTransferCcy, onMoveContinue, onMoveBack, onMoveConfirm, onMoveDone,
  validateMoveAmountBlur, openOpenAccount
} from '../actions.js';

// Purely a UI estimate for this prototype (no real fee model exists
// anywhere else in the app either) — shown only when the chosen currency
// or chain differs from the source account's default, since a same-
// currency/same-chain internal transfer wouldn't incur a network fee.
function estimateFee(amt) {
  return Math.max(0.5, amt * 0.001);
}

function balanceBox(w) {
  const rows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', { class: 'balance-row' }, [
      h('span', {}, `${ccy}  ${fmtNum(amt)}`),
      h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt)))
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      h('span', { class: 'name' }, w.name),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    h('div', { class: 'owner' }, w.owner || '—'),
    h('div', { class: 'balances' }, rows)
  ]);
}

function renderForm(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  const fromCcyOptions = Object.keys(curAccount.balances);
  if (!fromCcyOptions.includes(s.reportingCurrency)) fromCcyOptions.push(s.reportingCurrency);
  const availableAmt = m.ccy === s.reportingCurrency ? totalUSDFor(curAccount) : (curAccount.balances[m.ccy] || 0);
  const availableFmt = m.ccy === s.reportingCurrency ? fmtReporting(availableAmt) : fmtNum(availableAmt) + ' ' + m.ccy;

  const sourceDefaultCcy = Object.keys(curAccount.balances)[0];
  const sourceDefaultChain = CONFIG.NET[sourceDefaultCcy] || '';
  // The reporting currency (e.g. USD) isn't a chain asset — there's no
  // network to pick or a fee to estimate for it, unlike Send's From-account
  // ccy select this one always presets a real crypto ccy on entry, so this
  // only matters if the user actively picks the reporting currency here.
  const isChainAsset = m.ccy !== s.reportingCurrency;
  const showFee = isChainAsset && (m.ccy !== sourceDefaultCcy || m.chain !== sourceDefaultChain);
  const feeAmt = parseFloat(m.amount) || 0;

  const toOptions = s.wallets.filter((w) => w.id !== m.fromAccountId).map((w) => ({ value: 'account:' + w.id, label: w.name }));

  const toSelect = h('select', {
    onchange: (e) => {
      const v = e.target.value;
      if (v === '__addaccount__') openOpenAccount();
      else setMoveField('toKey', v);
    }
  }, [
    h('option', { value: '', selected: m.toKey === '' || undefined }, 'Select destination…'),
    ...toOptions.map((o) => h('option', { value: o.value, selected: o.value === m.toKey || undefined }, o.label)),
    h('option', { value: '__addaccount__' }, '+ Add account')
  ]);

  const ccySelect = h('select', { onchange: (e) => setTransferCcy(e.target.value) },
    fromCcyOptions.map((c) => h('option', { value: c, selected: c === m.ccy || undefined }, c)));

  const chainOptions = CONFIG.CHAINS[m.ccy] || (m.chain ? [m.chain] : []);
  const chainSelect = h('select', { onchange: (e) => setMoveField('chain', e.target.value) },
    chainOptions.map((c) => h('option', { value: c, selected: c === m.chain || undefined }, c)));

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, 'Transfer'),
    balanceBox(curAccount),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'To'),
      h('div', { class: 'field-row' }, [toSelect]),
      h('div', { class: 'field-error' }, m.errors.toKey || '')
    ]),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'Amount'),
      h('div', { class: 'field-row' }, [
        h('input', {
          placeholder: '0.00', value: m.amount,
          oninput: (e) => setSilent((st) => ({ move: { ...st.move, amount: e.target.value, errors: { ...st.move.errors, amount: null } } })),
          onblur: validateMoveAmountBlur
        }),
        ccySelect
      ]),
      h('div', { class: 'field-help' }, `Available: ${availableFmt}`),
      h('div', { class: 'field-error' }, m.errors.amount || '')
    ]),
    isChainAsset ? h('div', {}, [
      h('div', { class: 'card-label' }, 'Chain'),
      chainSelect
    ]) : null,
    showFee ? h('div', { class: 'field-help' }, `Est. network fee: ${fmtNum(estimateFee(feeAmt))} ${m.ccy}`) : null,
    h('button', { class: 'btn btn-primary', onclick: onMoveContinue }, 'Continue to review')
  ]);
}

function renderReview(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  let reviewTo = '';
  if (m.toKey) {
    const [, refId] = m.toKey.split(':');
    const w = s.wallets.find((w) => w.id === refId);
    reviewTo = w ? w.name : '';
  }
  const sourceDefaultCcy = Object.keys(curAccount.balances)[0];
  const sourceDefaultChain = CONFIG.NET[sourceDefaultCcy] || '';
  const isChainAsset = m.ccy !== s.reportingCurrency;
  const showFee = isChainAsset && (m.ccy !== sourceDefaultCcy || m.chain !== sourceDefaultChain);
  const amt = parseFloat(m.amount || 0);

  const row = (label, value) => h('div', { class: 'review-row' }, [
    h('span', { class: 'k' }, label),
    h('span', { class: 'v' }, value)
  ]);

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, 'Review transfer'),
    h('div', { class: 'card stack' }, [
      row('From', curAccount.name + ' · ' + m.ccy),
      row('To', reviewTo),
      row('Amount', fmtNum(amt) + ' ' + m.ccy),
      isChainAsset ? row('Chain', m.chain) : null,
      showFee ? row('Est. network fee', fmtNum(estimateFee(amt)) + ' ' + m.ccy) : null
    ]),
    m.errors.submit ? h('div', { class: 'field-error' }, m.errors.submit) : null,
    h('div', { class: 'btn-row' }, [
      h('button', { class: 'btn btn-secondary', disabled: m.sending, onclick: onMoveBack }, 'Back'),
      h('button', {
        class: 'btn btn-primary btn-emphasis',
        disabled: m.sending,
        onclick: m.sending ? null : onMoveConfirm
      }, m.sending ? 'Sending…' : 'Confirm transfer')
    ])
  ]);
}

function renderSuccess(s) {
  const t = s.move.resultTx;
  return h('div', { class: 'success-block' }, [
    h('div', { class: 'success-badge' }, h('div', { class: 'check' })),
    h('div', { class: 'success-title' }, 'Transfer complete'),
    h('div', { class: 'success-sub' }, `${t ? fmtNum(t.amount) + ' ' + t.ccy : ''} moved successfully`),
    h('div', { class: 'ref-card' }, [
      `Ref ${t ? t.id : ''}`, h('br'), `Hash ${t ? t.hash : ''}`
    ]),
    h('button', { class: 'btn btn-primary', onclick: onMoveDone }, 'Done')
  ]);
}

export function renderTransfer() {
  const s = getState();
  const step = s.move.step;
  const inner = step === 'review' ? renderReview(s) : step === 'success' ? renderSuccess(s) : renderForm(s);
  return h('div', { class: 'screen' }, [inner]);
}
